import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { SEOHead, pageSEO } from '../../utils/seo';
import {
  CheckCircle as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Code as CodeIcon,
  School as SchoolIcon,
  WorkOutline as WorkIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Star as StarIcon,
} from '@mui/icons-material';

import { usePagePerformance } from '../../utils/performance';
import CompanyTimeline from './CompanyTimeline';

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation('marketing');

  const teamMembers = [
    {
      id: 'ceo',
      name: t('about_team_member_1_name'),
      role: t('about_team_member_1_role'),
      avatar: 'AR',
      bio: t('about_team_member_1_bio'),
      skills: ['React', 'Laravel', 'AWS', 'TypeScript', 'Docker', 'Team Leadership'],
      testId: 'team-ceo',
    },
    {
      id: 'cto',
      name: t('about_team_member_2_name'),
      role: t('about_team_member_2_role'),
      avatar: 'SC',
      bio: t('about_team_member_2_bio'),
      skills: ['DevOps', 'Kubernetes', 'Azure', 'CI/CD', 'System Architecture', 'Security'],
      testId: 'team-cto',
    },
    {
      id: 'lead-dev',
      name: t('about_team_member_3_name'),
      role: t('about_team_member_3_role'),
      avatar: 'MJ',
      bio: t('about_team_member_3_bio'),
      skills: ['React', 'TypeScript', 'Material-UI', 'Redux', 'Testing', 'UI/UX'],
      testId: 'team-lead-dev',
    },
    {
      id: 'mobile-dev',
      name: t('about_team_member_4_name'),
      role: t('about_team_member_4_role'),
      avatar: 'LW',
      bio: t('about_team_member_4_bio'),
      skills: ['React Native', 'Flutter', 'iOS', 'Android', 'Firebase', 'App Store'],
      testId: 'team-mobile-dev',
    },
  ];

  const companyValues = [
    {
      icon: <SecurityIcon fontSize="large" />,
      title: t('about_value_1_title'),
      description: t('about_value_1_desc'),
    },
    {
      icon: <SpeedIcon fontSize="large" />,
      title: t('about_value_2_title'),
      description: t('about_value_2_desc'),
    },
    {
      icon: <GroupIcon fontSize="large" />,
      title: t('about_value_3_title'),
      description: t('about_value_3_desc'),
    },
    {
      icon: <TrendingUpIcon fontSize="large" />,
      title: t('about_value_4_title'),
      description: t('about_value_4_desc'),
    },
  ];

  const navigate = useNavigate();

  const handleContactUs = () => {
    navigate('/contact');
  };

  return (
    <>
      <SEOHead {...pageSEO.about} />
      <Box>
      {/* Hero Section */}
      <Box
        component="section"
        data-testid="about-hero"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 10 },
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="h1"
              component="h1"
              data-testid="about-hero-title"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              {t('about_page_hero_title')}
            </Typography>
            <Typography
              variant="h5"
              component="h2"
              data-testid="about-hero-subtitle"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.5rem' },
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                lineHeight: 1.6,
              }}
            >
              {t('about_page_hero_subtitle')}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Company Story Section */}
        <Box component="section" data-testid="company-story" sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h2"
                component="h2"
                data-testid="story-title"
                sx={{
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: 'text.primary',
                }}
              >
                {t('about_story_title')}
              </Typography>
              <Typography
                variant="body1"
                data-testid="story-content"
                sx={{ mb: 4, lineHeight: 1.7, fontSize: '1.1rem' }}
              >
                {t('about_story_p1')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 4, lineHeight: 1.7, fontSize: '1.1rem' }}
              >
                {t('about_story_p2')}
              </Typography>
              <List>
                {[
                  t('about_story_list_1'),
                  t('about_story_list_2'),
                  t('about_story_list_3'),
                  t('about_story_list_4'),
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item}
                      primaryTypographyProps={{ variant: 'body1', sx: { fontSize: '1rem' } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ flex: { xs: 1, md: '0 0 400px' } }}>
              <Box data-testid="story-visual">
                <CompanyTimeline />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Values Section */}
        <Box component="section" data-testid="company-values" sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              data-testid="values-title"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {t('about_values_title')}
            </Typography>
            <Typography
              variant="h6"
              data-testid="values-subtitle"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {t('about_values_subtitle')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              gap: 4
            }}
          >
            {companyValues.map((value, index) => (
              <Card
                key={index}
                data-testid={`value-${index + 1}`}
                sx={{
                  p: 3,
                  height: '100%',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: `${theme.palette.primary.main}20`,
                      color: theme.palette.primary.main,
                      flexShrink: 0,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {value.description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Team Section */}
        <Box component="section" data-testid="team-section" sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              data-testid="team-title"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {t('about_team_title')}
            </Typography>
            <Typography
              variant="h6"
              data-testid="team-subtitle"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {t('about_team_subtitle')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, 
              gap: 4
            }}
          >
            {teamMembers.map((member) => (
              <Card
                key={member.id}
                data-testid={member.testId}
                sx={{
                  textAlign: 'center',
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[6],
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Avatar
                  data-testid={`${member.testId}-avatar`}
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '1.5rem',
                    fontWeight: 600,
                  }}
                >
                  {member.avatar}
                </Avatar>
                <Typography
                  variant="h6"
                  component="h3"
                  data-testid={`${member.testId}-name`}
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {member.name}
                </Typography>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  data-testid={`${member.testId}-role`}
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  {member.role}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid={`${member.testId}-bio`}
                  sx={{ mb: 3, lineHeight: 1.5, flexGrow: 1 }}
                >
                  {member.bio}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  {member.skills.slice(0, 4).map((skill) => (
                    <Typography
                      key={skill}
                      variant="caption"
                      sx={{
                        bgcolor: 'grey.100',
                        color: 'text.secondary',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      }}
                    >
                      {skill}
                    </Typography>
                  ))}
                </Box>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Expertise Section */}
        <Box component="section" data-testid="expertise-section" sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              data-testid="expertise-title"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {t('about_expertise_title')}
            </Typography>
            <Typography
              variant="h6"
              data-testid="expertise-subtitle"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {t('about_expertise_subtitle')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
              gap: 4
            }}
          >
            {[
              {
                icon: <CodeIcon fontSize="large" />,
                title: t('about_expertise_1_title'),
                skills: ['React 18+', 'TypeScript', 'Material-UI', 'Redux Toolkit', 'Next.js', 'Progressive Web Apps'],
              },
              {
                icon: <WorkIcon fontSize="large" />,
                title: t('about_expertise_2_title'),
                skills: ['Laravel 10+', 'Node.js', 'RESTful APIs', 'GraphQL', 'Microservices', 'Database Design'],
              },
              {
                icon: <SchoolIcon fontSize="large" />,
                title: t('about_expertise_3_title'),
                skills: ['Docker', 'Kubernetes', 'AWS/Azure', 'CI/CD Pipelines', 'Monitoring', 'Security'],
              },
            ].map((expertise, index) => (
              <Card
                key={index}
                data-testid={`expertise-${index + 1}`}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: `${theme.palette.primary.main}20`,
                    color: theme.palette.primary.main,
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  {expertise.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  {expertise.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {expertise.skills.map((skill) => (
                    <Box key={skill} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StarIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                        {skill}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Box
          component="section"
          data-testid="about-cta"
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            mb: 8,
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            data-testid="about-cta-title"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t('about_cta_title')}
          </Typography>
          <Typography
            variant="h6"
            data-testid="about-cta-subtitle"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('about_cta_subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              data-testid="about-cta-contact"
              onClick={handleContactUs}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {t('about_cta_button_1')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              data-testid="about-cta-portfolio"
              onClick={handleContactUs}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              {t('about_cta_button_2')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default AboutPage;